import { dNotify } from 'packages/lib/common-ui/notification/deduped-notify';
import { z } from 'zod';
import { JobStatusType } from 'packages/lib/common-ui/state-icon';
import { VehicleType } from 'packages/lib/api/annotations/contract/enums';

export const GOJobPayloadSchema = z.object({
  batch_name: z.string(),
  workflow_name: z.string(),
  duration_in_seconds: z.string().or(z.number()),
  cluster_id: z.string().optional(),
  // Doesn't exist in older entries. Exists as of 7/6/2022 moving forward
  version: z.string().optional(),
  cuboid: z
    .object({
      x_center: z.number(),
      y_center: z.number(),
      z_center: z.number(),
      width: z.number(),
      length: z.number(),
      height: z.number(),
      yaw: z.number(),
    })
    .optional(),
});

export type GOJobPayload = z.infer<typeof GOJobPayloadSchema>;

export const isGOJobPayload = (payload: unknown): payload is GOJobPayload =>
  GOJobPayloadSchema.safeParse(payload).success;

export const PayloadErrorSchema = z.object({
  errorMsg: z.string().optional(),
});
export type PayloadError = z.infer<typeof PayloadErrorSchema>;

/**
 * Used to track items that are sent directly to annotations from LogMiner or Data Discovery.
 * Note that ALL images that match a single job's timestamp/log_name should be considered part of that 'job'.
 * That is because a job represents an entire Lidar scene. Therefore, many camera images might be part of this scene.
 Most properties can be null, because some older jobs exist with only a job_id, created_timestamp, and updated_timestamp, and everything else is null.
*/
export const JobStatusSchema = z.object({
  job_id: z.number(),
  job_name: z.string().nullable(),
  user_name: z.string().nullable(),
  timestamp: z.string().nullable(),
  log_name: z.string().nullable(),
  // TODO: add more types here later for other jobs, such as Lidar
  payload: z
    .string()
    .nullable()
    .transform((prev) => {
      if (prev === null) {
        return prev;
      }
      if (typeof prev === 'string') {
        let parsedJson: unknown;
        try {
          parsedJson = JSON.parse(prev);
        } catch (err) {
          dNotify.warning({
            message: `Failed to parse JobStatus.payload JSON for some API data:`,
            description: (err as Error).message,
          });
        }
        if (!parsedJson) {
          return null;
        }
        return GOJobPayloadSchema.or(PayloadErrorSchema)
          .nullable()
          .parse(parsedJson);
      }
      throw new Error('Invalid JobStatus.payload type');
    }),
  status: z.nativeEnum(JobStatusType).nullable(),
  created_timestamp: z.string(),
  updated_timestamp: z.string(),
});

export type JobStatus = z.infer<typeof JobStatusSchema>;

export const isJobStatus = (value: unknown): value is JobStatus =>
  JobStatusSchema.safeParse(value).success;

const JobStatusArraySchema = z.array(JobStatusSchema);

export const isJobStatusArray = (value: unknown): value is JobStatus[] => {
  const result = JobStatusArraySchema.safeParse(value);
  if (result.success) {
    return true;
  }

  dNotify.error({
    message: 'Failed to parse jobs with error:',
    description: String(result.error.toString()),
  });
  console.error('Failed to parse jobs with error:', result.error.format());

  return false;
};

export type QueryImageMetaData = {
  log_name: string; // "2021.07.21.19.16.50_g2h-veh-9004"
  camera_channel?: string; // "CAM_M_L2"    // Will not be present on GO mining query results
  vehicle_type?: VehicleType; // "gen2" -  Will not be present on data that was obtained via SQL query results (i.e. GO mining)
  timestamp: string; // "1626893362531130"
  image_timestamp?: string; // Present on GO mining table, but only if user includes it in their SQL query.
  relative_timestamp_us?: string; // Present on GO mining table, but only if user includes it in their SQL query.
  version?: string; // "0.1.0" - Present only for GO mining queries
  // GO mining fields also included with intersection type below
};
